<template>
  <CreateUpdateTemplate
    :routePreventDialog="routePreventDialog"
    :hasChanges="hasChanges"
    :customClass="'create-enquiry create-form'"
    v-if="
      getPermission('adjustment:create') || getPermission('adjustment:update')
    "
  >
    <template v-slot:header-title>
      <div class="form-action d-flex align-center justify-space-between">
        <div
          class="form-title d-flex"
          style="
            font-size: 24px;
            font-weight: 700;
            color: #24326d;
            letter-spacing: 1.5px;
            line-height: 1.11em;
          "
        >
          Adjustment for
          <div
            v-if="reSupplierId"
            class="ml-3 form-title-link cursor-pointer text-h5"
            v-on:click="openDialog('supplier')"
          >
            {{ reSupplier.company_name }}
          </div>
          <template v-else>
            <div
              class="ml-3 form-title-link cursor-pointer text-h5"
              v-on:click="openDialog('supplier')"
            >
              Select a Supplier
              <v-icon large color="cyan">mdi-account-check-outline</v-icon>
            </div>
          </template>
        </div>
        <div>
          <v-chip
            v-if="barcode"
            label
            color="chip-custom-blue"
            outlined
            class="text-white p-3 mr-4"
            style="font-size: 21px !important; font-weight: 700"
          >
            <template>
              {{ barcode }}
            </template>
          </v-chip>
        </div>
      </div>
    </template>
    <template v-slot:header-action>
      <div>
        <v-btn
          :disabled="formLoading"
          v-on:click="goBack()"
          class="custom-bold-button custom-grey-border"
          depressed
          >Cancel</v-btn
        >
        <v-btn
          class="custom-bold-button ml-4"
          depressed
          :disabled="!formValid || formLoading"
          :loading="formLoading"
          v-on:click="validateAdjustment()"
          color="cyan white--text"
        >
          Save
        </v-btn>
      </div>
    </template>
    <template v-slot:body>
      <div class="bg-white px-2">
        <perfect-scrollbar
          :options="{ suppressScrollX: true }"
          class="scroll"
          style="max-height: calc(100vh - 150px); position: relative"
        >
          <v-form
            ref="createAdjustmentForm"
            v-model.trim="formValid"
            lazy-validation
            v-on:submit.stop.prevent="validateAdjustment()"
          >
            <v-row>
              <v-col md="7">
                <label
                  class="font-size-16 font-weight-500"
                  for="adjustment-Description"
                  >Description</label
                >
                <TextAreaField
                  auto-grow
                  dense
                  filled
                  color="cyan"
                  v-model="adjustment.description"
                  placeholder="Description"
                  solo
                  flat
                  row-height="25"
                  counter="250"
                />
                <v-row class="mt-1">
                  <!-- Billing Location Sub-Card -->
                  <v-col md="6">
                    <v-card
                      flat
                      outlined
                      class="rounded-lg border-slate bg-slate-50 pa-3 h-100 shadow-0"
                    >
                      <div
                        class="d-flex align-center justify-space-between pb-2 mb-2 border-bottom border-slate-100"
                      >
                        <div class="d-flex align-center">
                          <v-icon small color="cyan darken-2" class="mr-2"
                            >mdi-map-marker-radius-outline</v-icon
                          >
                          <span
                            class="font-weight-700 color-custom-blue font-size-14 text-uppercase"
                          >
                            Billing Location
                          </span>
                        </div>
                        <v-btn
                          icon
                          x-small
                          color="cyan"
                          class="bg-white custom-grey-border"
                          v-on:click="openDialog('billing')"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </div>
                      <template v-if="reSupplierBilling?.id">
                        <div class="mb-2">
                          <div
                            v-if="reSupplierBilling.property_name"
                            class="font-weight-600 color-custom-blue font-size-13 d-flex align-center mb-1"
                          >
                            <v-icon small color="slate-600" class="mr-1"
                              >mdi-home-map-marker</v-icon
                            >
                            {{ reSupplierBilling.property_name }}
                          </div>
                          <div
                            v-if="reSupplierBilling.property_address"
                            class="d-flex align-start font-size-13 text-slate-700"
                          >
                            <v-icon small color="slate-500" class="mr-1 mt-1"
                              >mdi-map-marker-outline</v-icon
                            >
                            <div>{{ reSupplierBilling.property_address }}</div>
                          </div>
                        </div>
                      </template>
                      <template v-else-if="reSupplier?.id">
                        <div
                          class="d-flex align-center text-slate-500 font-size-13 py-2"
                        >
                          <v-icon small color="slate-400" class="mr-2"
                            >mdi-map-marker-outline</v-icon
                          >
                          <span>Please select billing location</span>
                        </div>
                      </template>
                      <template v-else>
                        <div
                          class="d-flex align-center text-slate-500 font-size-13 py-2"
                        >
                          <v-icon small color="slate-400" class="mr-2"
                            >mdi-account-outline</v-icon
                          >
                          <span>Please select supplier</span>
                        </div>
                      </template>
                    </v-card>
                  </v-col>

                  <!-- Contact Details Sub-Card -->
                  <v-col md="6">
                    <v-card
                      flat
                      outlined
                      class="rounded-lg border-slate bg-slate-50 pa-3 h-100 shadow-0"
                    >
                      <div
                        class="d-flex align-center justify-space-between pb-2 mb-2 border-bottom border-slate-100"
                      >
                        <div class="d-flex align-center">
                          <v-icon small color="cyan darken-2" class="mr-2"
                            >mdi-account-outline</v-icon
                          >
                          <span
                            class="font-weight-700 color-custom-blue font-size-14 text-uppercase"
                          >
                            Contact Details
                          </span>
                        </div>
                        <v-btn
                          icon
                          x-small
                          color="cyan"
                          class="bg-white custom-grey-border"
                          v-on:click="openDialog('billing-contact')"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </div>
                      <template v-if="reSupplierBillingContact?.id">
                        <div class="d-flex align-center mb-1 font-size-14">
                          <v-icon small color="slate-600" class="mr-2"
                            >mdi-account-circle-outline</v-icon
                          >
                          <show-value
                            :object="reSupplierBillingContact"
                            object-key="display_name"
                            label="Name"
                          />
                        </div>
                        <div class="d-flex align-center mb-1 font-size-14">
                          <v-icon small color="slate-500" class="mr-2"
                            >mdi-phone-outline</v-icon
                          >
                          <show-value
                            :object="reSupplierBillingContact"
                            object-key="primary_phone"
                            label="Contact No."
                          />
                        </div>
                        <div class="d-flex align-center mb-0 font-size-14">
                          <v-icon small color="slate-500" class="mr-2"
                            >mdi-email-outline</v-icon
                          >
                          <show-value
                            :object="reSupplierBillingContact"
                            object-key="primary_email"
                            label="Email"
                          />
                        </div>
                      </template>
                      <template v-else-if="reSupplier?.id">
                        <div
                          class="d-flex align-center text-slate-500 font-size-13 py-2"
                        >
                          <v-icon small color="slate-400" class="mr-2"
                            >mdi-account-circle-outline</v-icon
                          >
                          <span>Please select contact person</span>
                        </div>
                      </template>
                      <template v-else>
                        <div
                          class="d-flex align-center text-slate-500 font-size-13 py-2"
                        >
                          <v-icon small color="slate-400" class="mr-2"
                            >mdi-account-outline</v-icon
                          >
                          <span>Please select supplier</span>
                        </div>
                      </template>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
              <v-col md="5">
                <table width="100%" style="table-layout: fixed">
                  <!-- <tr>
                    <td width="25%">
                      <label class="font-size-16 font-weight-500 required"
                        >Type</label
                      >
                    </td>
                    <td>
                      <v-autocomplete
                        class="mt-2"
                        dense
                        filled
                        color="cyan"
                        item-color="cyan"
                        :items="Typeadjustment"
                        placeholder="Type"
                        solo
                        flat
                        item-text="name"
                        item-value="name"
                        v-model="adjustment.type"
                        :rules="[
                          validateRules.required(adjustment.type, 'Type'),
                        ]"
                      >
                      </v-autocomplete>
                    </td>
                  </tr> -->
                  <tr>
                    <td width="25%">
                      <label class="font-size-16 font-weight-500"
                        >Reference #</label
                      >
                    </td>
                    <td>
                      <div class="d-flex">
                        <TextField
                          id="adjust-reference"
                          dense
                          filled
                          placeholder="Reference #"
                          solo
                          flat
                          counter="50"
                          v-model.trim="adjustment.reference"
                          color="cyan"
                          :maxlength="100"
                          :rules="[
                            validateRules.minLength(
                              adjustment.reference,
                              'Reference #',
                              2
                            ),
                            validateRules.maxLength(
                              adjustment.reference,
                              'Reference #',
                              100
                            ),
                          ]"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label
                        class="font-size-16 font-weight-500"
                        for="adjust-Date"
                        >Date</label
                      >
                    </td>
                    <td>
                      <DatePicker
                        solo
                        mandatory
                        v-on:update:date-picker="updateDate"
                        :pageLoading="pageLoading"
                      ></DatePicker>
                    </td>
                  </tr>
                  <tr>
                    <td valign="top">
                      <label class="font-size-16 font-weight-500 ml-1 mt-2"
                        >Reason</label
                      >
                    </td>
                    <td>
                      <TextAreaField
                        auto-grow
                        dense
                        filled
                        color="cyan"
                        v-model="adjustment.reason"
                        placeholder="Reason"
                        solo
                        flat
                        row-height="25"
                        counter="100"
                      />
                    </td>
                  </tr>
                </table>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-card flat class="custom-grey-border remove-border-radius">
                  <v-card-title class="headline grey lighten-4">
                    <h3
                      class="font-weight-700 custom-headline color-custom-blue"
                    >
                      Choose Product / Part
                    </h3>
                  </v-card-title>
                  <v-card-text class="p-6 font-size-16">
                    <line-item
                      :key="`adjustment-line-item-${item_key}`"
                      :related-type="6"
                      :related-detail="{}"
                    />
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" ref="qtNote">
                <v-card flat class="remove-border-radius">
                  <v-expand-transition>
                    <v-layout class="mt-3">
                      <v-flex md12>
                        <DocumnetList
                          v-model="adjustment.attachments"
                          :document-type="59"
                        >
                        </DocumnetList>
                      </v-flex>
                    </v-layout>
                  </v-expand-transition>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </perfect-scrollbar>
        <supplier-select
          :dialog="reSupplierDialog"
          @close="closeSupplierDialog"
        />
        <SupplierAddressSelect
          :dialog="propertyDialog"
          :supplier-id="reSupplierId"
          v-on:close="propertyDialog = false"
          v-on:select="selectSupplierBilling"
        />
        <SupplierPersonSelect
          :dialog="contactDialog"
          :supplier-id="reSupplierId"
          v-on:close="contactDialog = false"
          v-on:select="selectSupplierBillingContact"
        />
      </div>
    </template>
  </CreateUpdateTemplate>
</template>

<style scoped>
.door-type-action {
  height: 20px;
  margin-left: 10px;
  background-color: #002557 !important;
  border-color: #002557 !important;
}
</style>
<script>
import AdjustmentLineItem from "@/view/pages/product/adjustment/Adjustment-Line-Item.vue";
import TextAreaField from "@/view/components/app-component/TextAreaField.vue";
import CreateUpdateTemplate from "@/view/pages/partials/Create-Update-Template.vue";
import { mapGetters } from "vuex";
import SupplierSelect from "@/view/pages/product/adjustment/SupplierSelect";
import {
  SET_SUPPLIER_DIALOG_STATUS,
  SET_SUPPLIER_BILLING,
  SET_SUPPLIER_BILLING_CONTACT,
  CLEAR_SUPPLIER,
} from "@/core/services/store/customer.module";
import { CLEAR_LINE_ITEM } from "@/core/services/store/line.item.module";
import { PreventDialogEventBus } from "@/core/lib/prevent.reload.lib";
import CommonMixin from "@/core/plugins/common-mixin";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { POST, QUERY } from "@/core/services/store/request.module";
import { ErrorEventBus, InitializeError } from "@/core/lib/message.lib";
import DatePicker from "@/view/pages/partials/Datepicker";
import ValidationMixin from "@/core/plugins/validation-mixin";
import ShowValue from "@/view/components/ShowValue.vue";
import SupplierAddressSelect from "@/view/components/SupplierAddressSelect.vue";
import SupplierPersonSelect from "@/view/components/SupplierPersonSelect.vue";

import DocumnetList from "@/view/pages/DocumnetList";

export default {
  mixins: [CommonMixin, ValidationMixin],
  name: "product-adjustment-create",
  data() {
    return {
      barcode: null,
      show_line_items: false,
      show_notes_attachment: false,
      serial_number_dialog: false,
      propertyDialog: false,
      contactDialog: false,
      dialog_key: Number(new Date()),
      item_key: Number(new Date()),
      adjustmentCreated: false,
      confirm_loading: false,
      adjustment: {
        reference: null,
        type: null,
        date: null,
        reason: null,
        description: null,
      },
      pageLoading: false,
      calculation_type: [
        {
          id: 1,
          text: "Subtraction",
        },
        {
          id: 2,
          text: "Addition",
        },
      ],
      Typeadjustment: [
        {
          id: 0,
          name: "Purchase Order",
        },
        {
          id: 1,
          name: "Delivery Order",
        },
        {
          id: 2,
          name: "Quotation",
        },
      ],
    };
  },
  components: {
    DatePicker,
    DocumnetList,
    CreateUpdateTemplate,
    "supplier-select": SupplierSelect,
    SupplierAddressSelect,
    SupplierPersonSelect,
    TextAreaField,
    "show-value": ShowValue,
    "line-item": AdjustmentLineItem,
  },
  watch: {
    reSupplierId: {
      deep: true,
      immediate: true,
      handler() {
        (this.item_key = Number(new Date())),
          this.$store.dispatch(CLEAR_LINE_ITEM);
      },
    },
  },
  methods: {
    selectSupplierBilling(row) {
      this.$store.commit(SET_SUPPLIER_BILLING, row);
    },
    selectSupplierBillingContact(row) {
      this.$store.commit(SET_SUPPLIER_BILLING_CONTACT, row);
    },
    openDialog(type) {
      this.dialog_key = Number(new Date());
      this.$store.commit(SET_SUPPLIER_DIALOG_STATUS, false);
      this.propertyType = null;
      this.propertyLabel = null;
      this.propertyDialog = false;
      this.contactType = null;
      this.contactDialog = false;

      if (type == "supplier") {
        this.$store.commit(SET_SUPPLIER_DIALOG_STATUS, true);
      } else {
        if (!this.reSupplierId) {
          ErrorEventBus.$emit("update:error", "Please Select Supplier.");
          return false;
        }
        if (type == "billing") {
          this.propertyType = "billing";
          this.propertyLabel = "Billing Location";
          this.propertyDialog = true;
        }
        if (type == "billing-contact") {
          this.contactType = "billing";
          this.contactDialog = true;
        }
        if (type == "property") {
          this.propertyType = "property";
          this.propertyLabel = "Site Location";
          this.propertyDialog = true;
        }
        if (type == "property-contact") {
          this.contactType = "property";
          this.contactDialog = true;
        }
      }
      this.$store.dispatch(CLEAR_LINE_ITEM);
    },
    closeSupplierDialog() {
      this.$store.commit(SET_SUPPLIER_DIALOG_STATUS, false);
    },

    validateAdjustment() {
      if (!this.lSelectedLineItem || !this.lSelectedLineItem.length) {
        ErrorEventBus.$emit("update:error", "Please Select Product or Part");
        return false;
      }

      let error = true;

      if (this.lSelectedLineItem?.length > 0) {
        this.lSelectedLineItem.forEach((row) => {
          if (!row.calculation_type) {
            ErrorEventBus.$emit(
              "update:error",
              `${row.barcode}, Please Select Type`
            );
            error = false;
            return false;
          }
          if (row?.adjusted_quantity < 1 || !row?.adjusted_quantity) {
            ErrorEventBus.$emit(
              "update:error",
              `${row.barcode}, Please Enter Adjusted Quantity`
            );
            error = false;
            return false;
          }
          if (
            row?.adjusted_quantity != row?.adjust_serial_array.length &&
            row?.isSerialized > 0
          ) {
            ErrorEventBus.$emit(
              "update:error",
              `${row.barcode}, Ensure that the adjusted quantity entered matches the Serial Number count`
            );
            error = false;
            return false;
          }
        });
      }

      if (!error) {
        return false;
      }

      const validateStatus = this.$refs.createAdjustmentForm.validate();

      const formErrors = this.validateForm(this.$refs.createAdjustmentForm);

      if (formErrors.length) {
        for (let i = 0; i < formErrors.length; i++) {
          ErrorEventBus.$emit("update:error", InitializeError(formErrors[i]));
        }
        return false;
      }

      if (validateStatus) {
        this.onSubmit();
      }
    },

    async onSubmit() {
      const request = {
        ...this.adjustment,
        attachments: this.adjustment.attachments,
        supplier_id: this.reSupplierId,
        property: this.reSupplierPropertyId,
        billing: this.reSupplierBillingId,
        property_contact_person: this.reSupplierPropertyContactId,
        calculation: this.vCalculations,
        billing_contact_person: this.reSupplierBillingContactId,
        line_items: this.lSelectedLineItem,
      };

      let requestType = POST;
      let requestURL = "adjustment";

      this.formLoading = true;

      this.$store
        .dispatch(requestType, { url: requestURL, data: request })
        .then(() => {
          this.$store.dispatch(CLEAR_SUPPLIER);
          this.$store.dispatch(CLEAR_LINE_ITEM);
          this.adjustmentCreated = true;
          this.$nextTick(() => {
            this.$router.push(this.getDefaultRoute("adjustment"));
          });
        })
        .catch(() => {
          this.formLoading = false;
        });
    },

    updateDate(date) {
      this.adjustment.date = date;
    },
    get_attributes() {
      this.pageLoading = true;
      this.$store
        .dispatch(QUERY, {
          url: "adjustment/options",
        })
        .then((response) => {
          this.barcode = response?.data?.barcode ?? null;
          const _this = this;
          _this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Adjustment", route: "adjustment" },
            { title: "Create" },
            { title: this.barcode },
          ]);
        })
        .catch((error) => {
          this.logError(error);
          this.goBack();
        })
        .finally(() => {
          this.pageLoading = false;
        });
    },
  },
  computed: {
    ...mapGetters([
      "reSupplier",
      "reSupplierProperty",
      "reSupplierPropertyContact",
      "reSupplierBilling",
      "reSupplierBillingContact",
      "reSupplierDialog",
      "reSupplierId",
      "lSelectedLineItem",
    ]),
  },

  mounted() {
    this.get_attributes();
  },

  destroyed() {
    this.$store.dispatch(CLEAR_SUPPLIER);
    this.$store.dispatch(CLEAR_LINE_ITEM);
  },
  beforeCreate() {
    this.$store.dispatch(CLEAR_SUPPLIER);
    this.$store.dispatch(CLEAR_LINE_ITEM);
  },
  created() {
    const _this = this;
    _this.productCreateadjustmentId = _this.lodash.toSafeInteger(
      _this.$route.query.generate_adjustment
    );
    _this.product_type = _this.$route.query.product_type;
  },
  beforeRouteLeave(to, from, next) {
    const _this = this;
    if (_this.adjustmentCreated) {
      next();
    } else {
      _this.routePreventDialog = true;
      PreventDialogEventBus.$on("stay-on-page", function () {
        _this.routePreventDialog = false;
        next(false);
      });
      PreventDialogEventBus.$on("leave-page", function () {
        _this.routePreventDialog = false;
        next();
      });
    }
  },
};
</script>
